import { useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import SpinnerOverlay from './SpinnerOverlay';
import { getStartOfSchoolYear } from './AdministratorDashboard/reportUtilities';
import { getConcerningEmotionsReport } from './BackendInterface';
import { buildReportRows } from './ConcerningEmotionsReportRows';
import { getEndOfDay, buildGradeOptions } from './ConcerningEmotionsReportUtilities';
import ConcerningEmotionsReportTable from './ConcerningEmotionsReportTable';
import ConcerningEmotionsReportFilters, { ALL } from './ConcerningEmotionsReportFilters';
import ConcerningEmotionsReportHeader from './ConcerningEmotionsReportHeader';
import { EmotionsReportClassroom } from '@peekapak/types/EmotionsReportRow';
import styles from './ConcerningEmotionsReport.module.scss';

const ConcerningEmotionsReport = (): JSX.Element => {
  const [classrooms, setClassrooms] = useState<EmotionsReportClassroom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState(ALL);
  const [selectedGrade, setSelectedGrade] = useState(ALL);
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);

  const today = useMemo(() => new Date(), []);
  const startOfSchoolYear = useMemo(() => getStartOfSchoolYear(), []);
  const [dateRange, setDateRange] = useState<DateRange>({ from: startOfSchoolYear, to: today });

  useEffect(() => {
    let mounted = true;

    getConcerningEmotionsReport('own')
      .then((data) => {
        if (mounted) {
          setClassrooms(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const classOptions = useMemo(
    () => [{ id: ALL, name: 'By class' }, ...classrooms.map((c) => ({ id: c.classroomId, name: c.classroomName }))],
    [classrooms],
  );

  const gradeOptions = useMemo(() => buildGradeOptions(classrooms), [classrooms]);

  const rows = useMemo(() => {
    const filteredClassrooms = classrooms.filter(
      (c) => selectedClassroomId === ALL || c.classroomId === selectedClassroomId,
    );
    const selectedRange = {
      start: (dateRange.from ?? startOfSchoolYear).getTime(),
      end: getEndOfDay(dateRange.to ?? today),
    };
    const allRows = buildReportRows(filteredClassrooms, selectedRange);
    return selectedGrade === ALL ? allRows : allRows.filter((row) => String(row.grade) === selectedGrade);
  }, [classrooms, selectedClassroomId, selectedGrade, dateRange, startOfSchoolYear, today]);

  return (
    <div className={styles.container}>
      <ConcerningEmotionsReportHeader scopeNote='Showing all students across your classes.' />
      <ConcerningEmotionsReportFilters
        classOptions={classOptions}
        gradeOptions={gradeOptions}
        selectedClassroomId={selectedClassroomId}
        setSelectedClassroomId={setSelectedClassroomId}
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
        dateRange={dateRange}
        setDateRange={setDateRange}
        startOfSchoolYear={startOfSchoolYear}
        today={today}
        isDisplayDayPicker={isDisplayDayPicker}
        setIsDisplayDayPicker={setIsDisplayDayPicker}
        isLoading={isLoading}
      />
      {isLoading ? (
        <SpinnerOverlay isShow />
      ) : loadError ? (
        <p role='alert'>Unable to load the concerning emotions report. Please try again.</p>
      ) : rows.length === 0 ? (
        <p>None of your students have checked in yet, there are no emotions to report.</p>
      ) : (
        <ConcerningEmotionsReportTable rows={rows} />
      )}
    </div>
  );
};

export default ConcerningEmotionsReport;
